import { useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { FeedPost, LiveFeed } from '@localagent/sdk';
import { MarkdownContent } from '@localagent/ui/components/markdown-content';
import { Tooltip } from '@localagent/ui/components/tooltip';
import { cn } from '@localagent/ui/lib/utils';
import { FeedComposer } from './FeedComposer';
import { useAgentDefinitions } from '../../hooks/useAgentDefinitions';

export function FeedPostCard({
  post,
  liveFeed,
  nested = false,
  className,
}: {
  post: FeedPost;
  liveFeed?: LiveFeed;
  nested?: boolean;
  className?: string;
}) {
  const [composer, setComposer] = useState<'reply' | 'quote' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { data: agentDefs } = useAgentDefinitions();
  const posterEmoji =
    post.poster_kind === 'persona' ? agentDefs?.find((p) => p.id === post.poster_id)?.emoji : undefined;
  const posterName = post.poster_kind === 'system' ? 'Artifacts' : post.poster_display_name;

  if (post.deleted) {
    return (
      <article
        data-testid="feed-post-card"
        data-post-id={post.id}
        data-deleted="true"
        className={cn('rounded-lg bg-card px-4 py-3 text-body text-muted-foreground shadow-sm', className)}
      >
        This post was removed.
      </article>
    );
  }

  async function run(op: 'like' | 'bookmark' | 'repost') {
    if (!liveFeed) {
      throw new Error('FeedPostCard engage requires a LiveFeed handle');
    }
    setError(null);
    try {
      await liveFeed.engage(post.id, op);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <article
      data-testid="feed-post-card"
      data-post-id={post.id}
      data-poster-kind={post.poster_kind}
      data-source-kind={post.source_kind ?? ''}
      className={cn('flex gap-3 rounded-lg bg-card px-4 py-3 shadow-sm', className)}
    >
      <FeedAvatar kind={post.poster_kind} name={posterName} emoji={posterEmoji} small={nested} />
      <div className="min-w-0 flex-1">
        <header className="mb-1 flex min-w-0 items-baseline gap-1.5">
          <span className="truncate text-label font-semibold" data-testid="feed-post-author">
            {posterName}
          </span>
          <span className="text-caption text-muted-foreground" aria-hidden>
            ·
          </span>
          <Link
            to={`/feed/${post.id}`}
            data-testid="feed-post-permalink"
            className="shrink-0 text-caption text-muted-foreground hover:underline"
          >
            {new Date(post.created_at).toLocaleString()}
          </Link>
        </header>
        {post.body ? <MarkdownContent content={post.body} /> : null}
        {post.quoted_post ? (
          <div className="mt-3" data-testid="feed-quoted-post">
            <FeedPostCard post={post.quoted_post} nested className="bg-muted shadow-none" />
          </div>
        ) : null}
        {post.reposted_post ? (
          <div className="mt-3" data-testid="feed-reposted-post">
            <FeedPostCard post={post.reposted_post} nested className="bg-muted shadow-none" />
          </div>
        ) : null}
        {post.source_kind === 'session' && post.source_id && post.source_status !== 'gone' ? (
          <Link
            to={`/chat/${post.source_id}`}
            data-testid="feed-source-link"
            className="mt-2 inline-block text-caption text-muted-foreground hover:underline"
          >
            Open session
          </Link>
        ) : null}
        {post.source_kind && post.source_status === 'gone' ? (
          <p className="mt-2 text-caption text-muted-foreground" data-testid="feed-source-gone">
            Source is no longer available.
          </p>
        ) : null}
        {liveFeed && post.poster_kind !== 'system' ? (
          <div className="mt-3 flex items-center gap-6 text-muted-foreground" data-testid="feed-post-actions">
            <FeedAction
              label="Reply"
              count={post.reply_count}
              tone="sky"
              active={composer === 'reply'}
              data-testid="feed-reply"
              onClick={() => setComposer(composer === 'reply' ? null : 'reply')}
            >
              <ReplyIcon />
            </FeedAction>
            <FeedAction
              label={post.viewer_reposted ? 'Undo repost' : 'Repost'}
              count={post.repost_count}
              tone="emerald"
              active={post.viewer_reposted}
              data-testid="feed-repost"
              onClick={() => {
                void run('repost');
              }}
            >
              <RepostIcon />
            </FeedAction>
            <FeedAction
              label={post.viewer_liked ? 'Unlike' : 'Like'}
              count={post.like_count}
              tone="rose"
              active={post.viewer_liked}
              data-testid="feed-like"
              data-liked={post.viewer_liked ? 'true' : 'false'}
              onClick={() => {
                void run('like');
              }}
            >
              <HeartIcon filled={post.viewer_liked} />
            </FeedAction>
            <FeedAction
              label="Quote"
              tone="sky"
              active={composer === 'quote'}
              data-testid="feed-quote"
              onClick={() => setComposer(composer === 'quote' ? null : 'quote')}
            >
              <QuoteIcon />
            </FeedAction>
            <FeedAction
              label={post.viewer_bookmarked ? 'Remove bookmark' : 'Bookmark'}
              tone="primary"
              active={post.viewer_bookmarked}
              className="ml-auto"
              data-testid="feed-bookmark"
              onClick={() => {
                void run('bookmark');
              }}
            >
              <BookmarkIcon filled={post.viewer_bookmarked} />
            </FeedAction>
          </div>
        ) : null}
        {error ? (
          <p className="mt-2 text-body text-destructive" role="alert">
            {error}
          </p>
        ) : null}
        {liveFeed && composer === 'reply' ? (
          <div className="mt-3" data-testid="feed-reply-composer">
            <FeedComposer
              liveFeed={liveFeed}
              kind="reply"
              parentId={post.id}
              onPublished={() => setComposer(null)}
            />
          </div>
        ) : null}
        {liveFeed && composer === 'quote' ? (
          <div className="mt-3" data-testid="feed-quote-composer">
            <FeedComposer
              liveFeed={liveFeed}
              kind="quote"
              quoteOfId={post.id}
              onPublished={() => setComposer(null)}
            />
          </div>
        ) : null}
      </div>
    </article>
  );
}

function FeedAvatar({
  kind,
  name,
  emoji,
  small,
}: {
  kind: FeedPost['poster_kind'];
  name: string;
  emoji: string | null | undefined;
  small: boolean;
}) {
  const glyph = kind === 'system' ? '🗂️' : emoji;
  return (
    <span
      data-testid="feed-post-avatar"
      data-poster-kind={kind}
      aria-hidden
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full font-semibold',
        small ? 'h-6 w-6 text-caption' : 'h-10 w-10 text-body',
        glyph ? 'bg-muted' : 'bg-primary/10 text-primary',
        glyph && (small ? 'text-body' : 'text-title-1'),
      )}
    >
      {glyph ?? name.trim().charAt(0).toUpperCase()}
    </span>
  );
}

type ActionTone = 'sky' | 'emerald' | 'rose' | 'primary';

const TONE_CLASS: Record<ActionTone, { text: string; halo: string; active: string }> = {
  sky: { text: 'hover:text-chart-5', halo: 'group-hover:bg-chart-5/10', active: 'text-chart-5' },
  emerald: { text: 'hover:text-chart-2', halo: 'group-hover:bg-chart-2/10', active: 'text-chart-2' },
  rose: { text: 'hover:text-chart-4', halo: 'group-hover:bg-chart-4/10', active: 'text-chart-4' },
  primary: { text: 'hover:text-primary', halo: 'group-hover:bg-primary/10', active: 'text-primary' },
};

function FeedAction({
  label,
  count,
  tone,
  active,
  className,
  children,
  ...props
}: {
  label: string;
  count?: number;
  tone: ActionTone;
  active: boolean;
  className?: string;
  children: ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>) {
  return (
    <Tooltip label={label} className={className}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={active}
        className={cn(
          'group inline-flex items-center gap-1.5 rounded-full text-caption tabular-nums transition-colors',
          active ? TONE_CLASS[tone].active : TONE_CLASS[tone].text,
        )}
        {...props}
      >
        <span
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors',
            TONE_CLASS[tone].halo,
          )}
        >
          {children}
        </span>
        {count !== undefined && count > 0 ? <span>{count}</span> : null}
      </button>
    </Tooltip>
  );
}

const ICON_PROPS = {
  xmlns: 'http://www.w3.org/2000/svg',
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function ReplyIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
    </svg>
  );
}

function RepostIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="m2 9 3-3 3 3" />
      <path d="M13 18H7a2 2 0 0 1-2-2V6" />
      <path d="m22 15-3 3-3-3" />
      <path d="M11 6h6a2 2 0 0 1 2 2v10" />
    </svg>
  );
}

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg {...ICON_PROPS} fill={filled ? 'currentColor' : 'none'}>
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
    </svg>
  );
}

function QuoteIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function BookmarkIcon({ filled }: { filled: boolean }) {
  return (
    <svg {...ICON_PROPS} fill={filled ? 'currentColor' : 'none'}>
      <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
    </svg>
  );
}
